import { pageurl } from '@constants/pageurl';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { type IPartyGuid } from '../../../interface/IAgents';
import { partyInitials } from '../../../utils/partyInitials';
import { pageSectionEnum } from '../../pages/landing-page';
import { Separator } from '../../utils/styled';
import ThemeToggle from '../../utils/theme-toggle';
import { HamburgerButton, HeaderContainer, LinkContainer, MobileMenu, MobileMenuLink, NavLinks } from './styled';

const HamburgerIcon = ({ isOpen }: { isOpen: boolean }) => (
	<svg
		width='24'
		height='24'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'
	>
		{isOpen ? (
			<>
				<line
					x1='18'
					y1='6'
					x2='6'
					y2='18'
				/>
				<line
					x1='6'
					y1='6'
					x2='18'
					y2='18'
				/>
			</>
		) : (
			<>
				<line
					x1='3'
					y1='6'
					x2='21'
					y2='6'
				/>
				<line
					x1='3'
					y1='12'
					x2='21'
					y2='12'
				/>
				<line
					x1='3'
					y1='18'
					x2='21'
					y2='18'
				/>
			</>
		)}
	</svg>
);

const Header = ({
	setPageSection,
	dataPartyGuid,
	logoUrl,
}: {
	setPageSection: (pageSection: string) => (dispatch: any) => void;
	dataPartyGuid: IPartyGuid | undefined;
	logoUrl?: string | null;
}) => {
	const navigate = useNavigate();
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	// Fall back to the tinted initials tile if the logo URL is absent or fails to
	// load (e.g. a broken/blocked image).
	const [logoBroken, setLogoBroken] = useState(false);

	useEffect(() => {
		setLogoBroken(false);
	}, [logoUrl]);

	const showLogo = Boolean(logoUrl) && !logoBroken;

	const toggleMobileMenu = () => {
		setIsMobileMenuOpen(!isMobileMenuOpen);
	};

	const closeMobileMenu = () => {
		setIsMobileMenuOpen(false);
	};

	return (
		<>
			<HeaderContainer className='shadow-sm'>
				<div
					style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '13px' }}
					onClick={() => {
						navigate(pageurl.OVERVIEW);
						setPageSection(pageSectionEnum.LANDING);
						closeMobileMenu();
					}}
					className='pt-3'
					role='button'
					tabIndex={0}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							navigate(pageurl.OVERVIEW);
							setPageSection(pageSectionEnum.LANDING);
							closeMobileMenu();
						}
					}}
					aria-label='Go to home page'
				>
					{showLogo ? (
						<img
							src={logoUrl ?? ''}
							alt=''
							aria-hidden='true'
							width={25}
							height={25}
							onError={() => setLogoBroken(true)}
							style={{
								width: '25px',
								height: '25px',
								borderRadius: '50%',
								objectFit: 'contain',
								background: 'var(--color-bg-elevated, #ffffff)',
								flex: 'none',
							}}
						/>
					) : (
						<span
							aria-hidden='true'
							style={{
								width: '25px',
								height: '25px',
								borderRadius: '50%',
								background: 'var(--color-success-bg, #e5f0e8)',
								color: 'var(--color-brand-primary)',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								fontSize: '9px',
								fontWeight: 700,
								letterSpacing: '0.02em',
								flex: 'none',
							}}
						>
							{partyInitials(dataPartyGuid?.data?.shortName || dataPartyGuid?.data?.longName || '')}
						</span>
					)}

					<p
						className='m-0'
						style={{ fontSize: '14px', color: 'var(--color-text-primary)' }}
					>
						{dataPartyGuid?.data?.longName}
					</p>
				</div>
				<Separator customwidth={20} />

				{/* Desktop Navigation */}
				<NavLinks className='ml-auto d-none d-md-flex align-items-center'>
					<LinkContainer to={pageurl.INSTRUCTIONS}>
						<p
							className='m-0 mt-3'
							style={{ fontSize: '14px', color: 'var(--color-text-primary)' }}
						>
							How it works
						</p>
					</LinkContainer>
					<Separator customwidth={15} />
					<ThemeToggle />
				</NavLinks>

				{/* Mobile Navigation Controls */}
				<div className='ml-auto d-flex d-md-none align-items-center'>
					<ThemeToggle />
					<HamburgerButton
						onClick={toggleMobileMenu}
						aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
						aria-expanded={isMobileMenuOpen}
					>
						<HamburgerIcon isOpen={isMobileMenuOpen} />
					</HamburgerButton>
				</div>
			</HeaderContainer>

			{/* Mobile Menu */}
			<MobileMenu
				isOpen={isMobileMenuOpen}
				aria-hidden={!isMobileMenuOpen}
			>
				<MobileMenuLink
					to={pageurl.INSTRUCTIONS}
					onClick={closeMobileMenu}
				>
					How it works
				</MobileMenuLink>
			</MobileMenu>
		</>
	);
};

export default Header;
